import { X, AlertTriangle } from 'lucide-react';
import { useState } from 'react';

interface Props {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: () => Promise<void>;
    jobNumber: string;
    taskCount?: number;
    incompleteATRItems?: Array<{ id: number; asset: string }>;
}

export default function ConfirmCompleteModal({ 
    isOpen, 
    onClose, 
    onConfirm, 
    jobNumber, 
    taskCount = 0,
    incompleteATRItems = []
}: Props) {
    const [loading, setLoading] = useState(false);

    const handleConfirm = async () => {
        setLoading(true);
        await onConfirm();
        setLoading(false);
    };

    // Check for validation issues
    const hasNoTasks = taskCount === 0;
    const hasIncompleteATRItems = incompleteATRItems.length > 0;
    const hasValidationIssues = hasNoTasks || hasIncompleteATRItems;

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-md">
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Complete Job Card</h2>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-500 dark:text-gray-300 dark:hover:text-gray-400"
                    >
                        <X size={24} />
                    </button>
                </div>

                <div className="mb-6">
                    <p className="text-gray-600 dark:text-gray-400 mb-4">
                        Are you sure you want to complete job card <span className="font-semibold">{jobNumber}</span>?
                    </p>
                    
                    {/* Validation Warnings */}
                    {hasValidationIssues && (
                        <div className="mb-4 p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                            <div className="flex items-start gap-3">
                                <AlertTriangle className="text-yellow-600 dark:text-yellow-400 mt-0.5 flex-shrink-0" size={20} />
                                <div className="flex-1">
                                    <h3 className="text-sm font-medium text-yellow-800 dark:text-yellow-200 mb-2">
                                        Please review the following issues:
                                    </h3>
                                    <ul className="space-y-1 text-sm text-yellow-700 dark:text-yellow-300">
                                        {hasNoTasks && (
                                            <li className="flex items-start gap-2">
                                                <span className="text-red-500 dark:text-red-400">•</span>
                                                <span>No tasks have been added to this job card</span>
                                            </li>
                                        )}
                                        {hasIncompleteATRItems && (
                                            <li className="flex items-start gap-2">
                                                <span className="text-orange-500 dark:text-orange-400">•</span>
                                                <span>
                                                    {incompleteATRItems.length} ATR item{incompleteATRItems.length > 1 ? 's' : ''} not completed:
                                                </span>
                                            </li>
                                        )}
                                    </ul>
                                    {hasIncompleteATRItems && (
                                        <div className="mt-2 ml-4">
                                            <ul className="text-xs text-yellow-600 dark:text-yellow-400 space-y-1">
                                                {incompleteATRItems.slice(0, 3).map((item) => (
                                                    <li key={item.id} className="truncate">
                                                        • {item.asset}
                                                    </li>
                                                ))}
                                                {incompleteATRItems.length > 3 && (
                                                    <li className="text-yellow-500 dark:text-yellow-300">
                                                        ... and {incompleteATRItems.length - 3} more
                                                    </li>
                                                )}
                                            </ul>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="text-sm text-gray-500 dark:text-gray-300">
                        This will:
                    </div>
                    <ul className="list-disc ml-8 mt-2 space-y-1 text-sm text-gray-500 dark:text-gray-300">
                        <li>Mark the job card as completed</li>
                        <li>Send email notifications to relevant parties</li>
                        <li>Make the job card no longer editable</li>
                    </ul>
                </div>

                <div className="flex justify-end gap-3">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleConfirm}
                        disabled={loading || hasNoTasks}
                        className={`px-4 py-2 text-white rounded-lg transition-colors duration-200 ${
                            hasNoTasks 
                                ? 'bg-gray-400 cursor-not-allowed dark:bg-gray-600' 
                                : 'bg-green-600 hover:bg-green-700 dark:bg-green-700 dark:hover:bg-green-600'
                        } ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                        {loading ? 'Completing...' : hasNoTasks ? 'Cannot Complete (No Tasks)' : 'Complete Job Card'}
                    </button>
                </div>
            </div>
        </div>
    );
} 